import React, { useEffect, useMemo, useState } from 'react'
import pa from '../ProductActivityPage.module.css'
import styles from './Pallets.module.css'
import os from './Orders.module.css'
import ls from './List.module.css'
import { ExportIcon, HeaderAction } from './chrome'
import { headerStyles } from '../../components/PageHeader'
import { workbook, XLSX_TYPE } from '../batches/xlsx'
import { fileTask, useTasks } from '../../components/TaskCentre'
import { useHelpPref } from '../../lib/preferences'
import { SkeletonRows } from '../../components/pending'
import { hasCapability, useAccess } from '../../lib/access'
import { fmtDay } from './model'
import { OrderModal } from './OrderModal'
import { dropUrlParams, oneOf, urlParam } from './stock'
import {
  MODE_FILTERS,
  ORDER_VIEWS,
  PROGRESS_FILTERS,
  approvalChip,
  boxesTriple,
  countLine,
  emptyNote,
  isOverdue,
  isUnscheduled,
  modeText,
  productsText,
  stateChips,
  todayIso,
  viewCounts,
  visibleOrders,
  applyFilters,
  type Chip,
  type OrderRow,
  type OrdersViewKey,
  type ProgressFilter,
} from './orders'
import { openableRow } from '../../lib/openableRow'

// The Orders list (V4 Stock → Orders): one row per order, dispatch date
// first, read forward from today. Every figure and state is the API's;
// the view, the date scope and lateness are worked out here as V4 does.

const ChipEl = ({ c }: { c: Chip }) => (
  <span className={styles.chip} style={{ background: c.bg, color: c.ink }}>
    {c.label}
  </span>
)

export function OrdersView({ user, orders, onChanged, showingLoading }: { user: any; orders: OrderRow[]; onChanged: () => void; showingLoading?: boolean }) {
  const [view, setView] = useState<OrdersViewKey>(() => oneOf(urlParam('view'), ORDER_VIEWS.map(([k]) => k), 'active'))
  const [q, setQ] = useState('')
  const [customer, setCustomer] = useState('')
  const [mode, setMode] = useState('')
  const [progress, setProgress] = useState<ProgressFilter>(() => oneOf(urlParam('progress'), PROGRESS_FILTERS.map(([k]) => k), ''))
  // An order id, 'new' for New order, or null
  const [openId, setOpenId] = useState<string | null>(() => urlParam('open'))
  const [toast, setToast] = useState('')
  const [helpOn, toggleHelp] = useHelpPref(user, 'orders')
  const { startTask } = useTasks()
  const mayOrder = hasCapability(useAccess(), 'create_edit_orders')

  // A deep link from the Overview (handoff §7n.1a) sets where this starts.
  useEffect(() => dropUrlParams(['view', 'progress', 'open']), [])

  useEffect(() => {
    if (!toast) return
    const t = setTimeout(() => setToast(''), 3400)
    return () => clearTimeout(t)
  }, [toast])

  const today = todayIso()
  const filters = { customer, mode, progress, q }
  const shown = visibleOrders(orders, view, filters, today)
  const counts = viewCounts(applyFilters(orders, filters), today)
  const customers = useMemo(() => {
    const m = new Map<string, string>()
    for (const o of orders) if (o.customer_id) m.set(o.customer_id, o.customer_name || o.customer_id)
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]))
  }, [orders])

  const exportRows = () => {
    const rows = [
      ['Dispatch date', 'Order', 'Customer PO', 'Customer', 'Destination', 'Mode', 'Local / export', 'Products', 'Ordered', 'Allocated', 'Dispatched', 'Unallocated', 'Approval', 'Allocation', 'Preparation', 'Dispatch', 'Invoiced'],
      ...shown.map((o) => [
        o.requested_dispatch_date || '',
        o.order_reference,
        o.customer_po || '',
        o.customer_name || '',
        o.destination_name || '',
        o.transport_mode || '',
        o.local_export || '',
        o.varieties.join(' + '),
        o.boxes.ordered,
        o.boxes.reserved + o.boxes.dispatched,
        o.boxes.dispatched,
        o.boxes.unallocated,
        approvalChip(o.approval_status).label,
        o.states.allocation.label,
        o.states.preparation.label,
        o.states.dispatch.label,
        o.states.invoiced.label,
      ]),
    ]
    const boxes = shown.reduce((a, o) => a + o.boxes.ordered, 0)
    const note = shown.length + ' order' + (shown.length === 1 ? '' : 's') + ' as shown · ' + boxes + ' boxes ordered'
    startTask(fileTask('Export orders', note, 'orders.xlsx', XLSX_TYPE, () => workbook([{ name: 'Orders', rows }])))
  }

  return (
    <div className={styles.wrap}>
      <div className={ls.toolbar}>
        <input className={`${ls.search} ${ls.searchOrders}`} placeholder="Order, customer, PO…" aria-label="Search orders" value={q} onChange={(e) => setQ(e.target.value)} />
        <select className={ls.select} aria-label="Customer" value={customer} onChange={(e) => setCustomer(e.target.value)}>
          <option value="">All customers</option>
          {customers.map(([id, name]) => (
            <option key={id} value={id}>
              {name}
            </option>
          ))}
        </select>
        <select className={ls.select} aria-label="Local or export" value={mode} onChange={(e) => setMode(e.target.value)}>
          {MODE_FILTERS.map(([v, l]) => (
            <option key={v} value={v}>
              {l}
            </option>
          ))}
        </select>
        <select className={ls.select} aria-label="Progress" value={progress} onChange={(e) => setProgress(e.target.value as ProgressFilter)}>
          {PROGRESS_FILTERS.map(([v, l]) => (
            <option key={v} value={v}>
              {l}
            </option>
          ))}
        </select>
        <button type="button" className={ls.helpPill} onClick={toggleHelp} aria-expanded={helpOn}>
          {helpOn ? 'Hide help' : 'How orders work'}
        </button>
        <button type="button" className={`${ls.exportBtn} ${ls.end}`} onClick={exportRows}>
          <ExportIcon />
          Export current results
        </button>
        {mayOrder && (
          <HeaderAction>
            <button type="button" className={headerStyles.btnPrimary} onClick={() => setOpenId('new')}>
              New order
            </button>
          </HeaderAction>
        )}
      </div>

      {helpOn && <OrdersHelp onClose={toggleHelp} />}

      <div className={ls.card}>
        <div className={`${ls.viewHead} ${os.listMin}`}>
          <div className={ls.viewTrack} role="tablist" aria-label="Order view">
            {ORDER_VIEWS.map(([k, l]) => (
              <button key={k} type="button" role="tab" aria-selected={view === k} className={view === k ? ls.viewTabOn : ls.viewTab} onClick={() => setView(k)}>
                {l}
                <span className={ls.viewCount}>{counts[k]}</span>
              </button>
            ))}
          </div>
          <span className={ls.viewLine} data-testid="orders-count">
            {countLine(shown)}
          </span>
        </div>
        <div className={`${os.listGrid} ${styles.head}`}>
          <span>Dispatch date</span>
          <span>Order</span>
          <span>Customer → destination</span>
          <span>Transport</span>
          <span title="ordered / allocated / dispatched">Boxes ord / alloc / disp</span>
          <span>Approval</span>
          <span>Allocation</span>
          <span>Preparation</span>
          <span>Dispatch</span>
          <span>Invoiced</span>
        </div>
        {showingLoading ? (
          <SkeletonRows rowClass={`${os.listGrid} ${styles.line}`} cols={10} label="Loading orders" />
        ) : shown.length === 0 ? (
          <div className={ls.empty}>{emptyNote(orders.length)}</div>
        ) : (
          shown.map((o) => {
            const [alloc, prep, disp, inv] = stateChips(o.states)
            const late = isOverdue(o, today)
            return (
              <div key={o.id} className={`${os.listGrid} ${styles.line} ${styles.clickable}`} {...openableRow(() => setOpenId(o.id))} data-testid="order-row">
                <span className={late ? os.late : isUnscheduled(o) ? os.undated : styles.strong}>
                  {o.requested_dispatch_date ? fmtDay(o.requested_dispatch_date) : 'Not scheduled'}
                </span>
                <span className={styles.stack}>
                  <span className={os.ref}>{o.order_reference}</span>
                  {o.customer_po && <span className={os.po}>PO {o.customer_po}</span>}
                </span>
                <span className={styles.stack}>
                  <span className={styles.ellipsis}>{o.customer_name || '—'}</span>
                  {o.destination_name && <span className={`${styles.sub} ${styles.ellipsis}`}>→ {o.destination_name}</span>}
                </span>
                <span className={styles.stack}>
                  <span className={styles.ellipsis}>{modeText(o.transport_mode, o.local_export)}</span>
                  <span className={`${styles.sub} ${styles.ellipsis}`}>{productsText(o)}</span>
                </span>
                <span className={styles.stack}>
                  <span className={os.num}>{boxesTriple(o.boxes)}</span>
                  {o.exceptions.length > 0 && (
                    <span className={os.exc}>
                      {o.exceptions.length} exception{o.exceptions.length === 1 ? '' : 's'}
                    </span>
                  )}
                </span>
                <span>
                  <ChipEl c={approvalChip(o.approval_status)} />
                </span>
                <span>
                  <ChipEl c={alloc} />
                </span>
                <span>
                  <ChipEl c={prep} />
                </span>
                <span>
                  <ChipEl c={disp} />
                </span>
                <span>
                  <ChipEl c={inv} />
                </span>
              </div>
            )
          })
        )}
      </div>

      {openId && (openId !== 'new' || mayOrder) && (
        <OrderModal
          user={user}
          orderId={openId === 'new' ? null : openId}
          onClose={() => setOpenId(null)}
          onChanged={(msg) => {
            if (msg) setToast(msg)
            onChanged()
          }}
          onNotice={setToast}
        />
      )}
      {toast && (
        <div className={pa.toast} role="status">
          {toast}
        </div>
      )}
    </div>
  )
}

function OrdersHelp({ onClose }: { onClose: () => void }) {
  return (
    <div className={ls.help} data-testid="orders-help">
      <div className={ls.helpHead}>
        <span className={ls.helpTitle}>How orders work</span>
        <span className={ls.helpLead}>The list reads forward from today. Late rows are red, undated rows sort last.</span>
        <button type="button" className={ls.helpX} title="Hide help" aria-label="Hide help" onClick={onClose}>
          ×
        </button>
      </div>
      <div className={ls.helpItem}>
        <span>One row is one order to one destination</span>
        <p>
          It carries lines: variety, box type, required lid, quantity. A single destination and one dispatch date. The dispatch date is when the order
          leaves, which is why the table leads with it and sorts ascending.
        </p>
      </div>
      <div className={ls.helpItem}>
        <span>The five state columns are its life</span>
        <p>
          Approval → Allocation → Preparation → Dispatch → Invoiced, read left to right. All five are computed from what has actually happened and none
          can be set by hand: you make an order ready by reserving stock and confirming pallets, not by marking it ready. An order is complete only
          when all five are done: until then it stays in Active.
        </p>
        <div className={ls.lines}>
          <div>Awaiting · Approved · Returned · Reapproval required</div>
          <div>Unallocated · Partly · Fully allocated</div>
          <div>Not prepared · In preparation · Prepared</div>
          <div>Dispatch moves only when a load departs</div>
          <div>To invoice appears once boxes have departed</div>
        </div>
      </div>
      <div className={ls.helpItem}>
        <span>Ordered / allocated / dispatched</span>
        <p>
          Three different commitments. <strong>Ordered</strong> is what the customer asked for. <strong>Allocated</strong> is reserved against specific
          stock: a promise, not a movement. <strong>Dispatched</strong> is what physically left on a confirmed load. The red figure under a row is the{' '}
          <strong>unallocated shortage</strong>: boxes with no stock behind them yet, which is not the same as not prepared. Stock can be reserved and
          still not on a pallet.
        </p>
      </div>
      <div className={ls.helpItem}>
        <span>Why an order returns for reapproval</span>
        <p>
          Editing an approved order in a way that changes the commercial commitment. Quantity, price, destination or dispatch date. Sends it back for
          reapproval. The order shows what changed against the approved version, so the approver sees the difference rather than the whole order
          again.
        </p>
      </div>
    </div>
  )
}
